<ng-container *transloco="let t">
  <mat-card class="margin-xs-bottom cs-card">
    <mat-card-header>
      <mat-card-title class="gap-xs">
        <mat-icon class="material-symbols-outlined">{{
          inquiry?.kind === 'return_request'
            ? 'assignment_return'
            : inquiry?.kind === 'cancellation_conflict'
              ? 'warning'
              : inquiry?.category === 'cancel'
                ? 'cancel'
                : 'contact_support'
        }}</mat-icon>
        {{ t('notifications.order_inquiry.title', { orderNumber: inquiry?.orderNumber ?? '' }) }}
      </mat-card-title>
    </mat-card-header>
    <mat-card-content>
      @if (inquiry; as inquiry) {
        <div class="inquiry-meta">
          <div><strong>{{ t('notifications.order_inquiry.question') }}:</strong> {{ t(labelKey) }}</div>
          @if (inquiry.conflictReference) {
            <div><strong>{{ t('notifications.order_inquiry.conflict_reference') }}:</strong> {{ inquiry.conflictReference }}</div>
          }
          <div><strong>{{ t('notifications.order_inquiry.requested_by') }}:</strong> {{ inquiry.requestedByName }}</div>
          @if (inquiry.vesselName) {
            <div><strong>{{ t('notifications.order_inquiry.vessel') }}:</strong> {{ inquiry.vesselName }}</div>
          }
          <div><strong>{{ t('notifications.order_inquiry.supplier') }}:</strong> {{ inquiry.supplierName }}</div>
          <div><strong>{{ t('notifications.order_inquiry.asked_at') }}:</strong> {{ inquiry.createdAt | date: 'short' }}</div>
        </div>

        @if (inquiry.items.length) {
          <table class="inquiry-items">
            <thead>
              <tr>
                <th>{{ t('notifications.order_inquiry.article_number') }}</th>
                <th>{{ t('notifications.order_inquiry.article') }}</th>
                <th>{{ t('notifications.order_inquiry.size') }}</th>
                <th class="inquiry-items__qty">{{ t('notifications.order_inquiry.quantity') }}</th>
              </tr>
            </thead>
            <tbody>
              @for (item of inquiry.items; track item.orderItemId) {
                <tr>
                  <td>{{ item.articleNumber ?? '—' }}</td>
                  <td>{{ item.name }}</td>
                  <td>{{ item.size ?? '—' }}</td>
                  <td class="inquiry-items__qty">{{ item.quantity }}</td>
                </tr>
              }
            </tbody>
          </table>
        }

        @if (hasConversation) {
          @if (decisionKey; as key) {
            <div class="inquiry-decision">
              <strong>{{ t(key) }}</strong>
            </div>
          }
          <app-order-inquiry-thread class="inquiry-thread" [messages]="inquiry.messages" [own]="inquiry.viewerSide" [questionLabel]="questionLabel" />
          @if (inquiry.closedAt) {
            <p class="inquiry-new-reply cs-banner">
              <mat-icon class="material-symbols-outlined">lock</mat-icon>
              {{ t('notifications.order_inquiry.closed_by', { name: inquiry.closedByName, date: (inquiry.closedAt | date: 'short') }) }}
            </p>
          } @else if (inquiry.viewerSide === 'supplier' && inquiry.waitingFor === 'supplier') {
            <p class="inquiry-new-reply cs-banner cs-banner--warning">
              <mat-icon class="material-symbols-outlined">mark_chat_unread</mat-icon>
              {{ t('notifications.order_inquiry.requester_replied', { name: inquiry.requestedByName }) }}
            </p>
          }
        } @else if (inquiry.question) {
          <blockquote class="inquiry-question">{{ inquiry.question }}</blockquote>
        }

        @if (isConflict) {
          <p>{{ t('notifications.order_inquiry.conflict_text', { supplier: inquiry.supplierName }) }}</p>
        } @else if (isCancellationRequest && inquiry.canAnswer) {
          <p>{{ t('notifications.order_inquiry.cancel_text') }}</p>
        }

        @if (inquiry.audience === 'administration' && inquiry.canAnswer && !isConflict) {
          <p class="text-muted">{{ t('notifications.order_inquiry.contact_supplier', { supplier: inquiry.supplierName }) }}</p>
        }

        @if (inquiry.answer && !hasConversation) {
          <div class="inquiry-answer">
            <strong>{{ t('notifications.order_inquiry.answer') }}</strong>
            @if (decisionKey; as key) {
              <div>
                <strong>{{ t(key) }}</strong>
              </div>
            }
            <div>{{ inquiry.answer }}</div>
            <small class="text-muted">{{ inquiry.answeredByName }} · {{ inquiry.answeredAt | date: 'short' }}</small>
          </div>
        } @else if (inquiry.canAnswer && isCancellationRequest) {
          <mat-form-field class="inquiry-answer-field cs-field">
            <mat-label>{{ t('notifications.order_inquiry.cancel_reason') }}</mat-label>
            <textarea matInput rows="3" maxlength="2000" [(ngModel)]="answer"></textarea>
            <mat-hint>{{ t('notifications.order_inquiry.cancel_reason_hint') }}</mat-hint>
          </mat-form-field>
          <div class="flex space-between gap-xs">
            <button mat-stroked-button color="primary" class="cs-button" type="button" [disabled]="submitting || !answer.trim()" (click)="submit('rejected')">
              {{ t('notifications.order_inquiry.reject_cancellation') }}
            </button>
            <button mat-raised-button color="warn" class="cs-button" type="button" [disabled]="submitting" (click)="cancelOrder()">
              {{ t('notifications.order_inquiry.cancel_order') }}
            </button>
          </div>
        } @else if (inquiry.canAnswer && isConflict) {
          <mat-form-field class="inquiry-answer-field cs-field">
            <mat-label>{{ t('notifications.order_inquiry.conflict_note') }}</mat-label>
            <textarea matInput rows="3" maxlength="2000" [(ngModel)]="answer"></textarea>
          </mat-form-field>
          <div class="flex space-between">
            <span></span>
            <button mat-raised-button color="primary" class="cs-button" type="button" [disabled]="submitting" (click)="submit('cleared')">
              {{ t('notifications.order_inquiry.mark_cleared') }}
            </button>
          </div>
        } @else if (inquiry.canAnswer) {
          <mat-form-field class="inquiry-answer-field cs-field">
            <mat-label>{{ t('notifications.order_inquiry.answer') }}</mat-label>
            <textarea matInput rows="4" maxlength="2000" [(ngModel)]="answer"></textarea>
            <mat-hint>{{ t('notifications.order_inquiry.answer_hint_conversation') }}</mat-hint>
          </mat-form-field>
          <div class="flex space-between">
            <span></span>
            <button mat-raised-button color="primary" class="cs-button" type="button" [disabled]="submitting || !answer.trim()" (click)="submit()">
              {{ t('notifications.order_inquiry.send_answer') }}
            </button>
          </div>
        } @else if (inquiry.canReply) {
          <!-- After the first answer both sides write on (SHIP-1967) -->
          <mat-form-field class="inquiry-answer-field cs-field">
            <mat-label>{{ t('notifications.order_inquiry.reply') }}</mat-label>
            <textarea matInput rows="3" maxlength="2000" [(ngModel)]="reply"></textarea>
            <mat-hint>{{ t(inquiry.viewerSide === 'requester' ? 'notifications.order_inquiry.reply_hint_requester' : 'notifications.order_inquiry.reply_hint') }}</mat-hint>
          </mat-form-field>
          <div class="flex space-between gap-xs">
            @if (inquiry.canClose) {
              <button mat-stroked-button color="primary" class="cs-button" type="button" [disabled]="submitting" (click)="closeConversation()">
                {{ t('notifications.order_inquiry.close') }}
              </button>
            } @else {
              <span></span>
            }
            <button mat-raised-button color="primary" class="cs-button" type="button" [disabled]="submitting || !reply.trim()" (click)="sendReply()">
              {{ t('notifications.order_inquiry.send_reply') }}
            </button>
          </div>
        } @else if (!inquiry.answer && !inquiry.closedAt) {
          <p class="text-muted">{{ t('notifications.order_inquiry.waiting') }}</p>
        }
      } @else if (loadFailed) {
        <p class="text-muted">{{ t('notifications.order_inquiry.load_failed') }}</p>
      } @else {
        <mat-progress-bar mode="indeterminate" />
      }
    </mat-card-content>
  </mat-card>
</ng-container>
